// <video-banner> — declarative Shadow DOM.
// The shadow root (markup + styles) is built by the browser from the
// <template shadowrootmode="open"> in the Liquid block. This script injects
// the video source + poster from data-* attributes, then starts playback.
//
// Autoplay is triggered programmatically (not via the `autoplay` attribute) so
// we can observe the play() Promise. iOS blocks muted autoplay in Low Power
// Mode — that is expected iOS behaviour, not a bug. When it happens the poster
// frame keeps the banner from looking empty, and we resume playback silently on
// the visitor's first interaction with the page.

class VideoBanner extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const src = this.dataset.src;
    const video = this.shadowRoot && this.shadowRoot.querySelector('video');
    if (!src || !video) return;

    if (this.dataset.poster) video.poster = this.dataset.poster;

    const source = document.createElement('source');
    source.src = src;
    source.type = 'video/mp4';
    video.appendChild(source);
    video.load();

    this._video = video;
    this._tryPlay();
  }

  _tryPlay() {
    const video = this._video;
    const attempt = video.play();
    // Older browsers return undefined from play(); nothing to handle there.
    if (!attempt || typeof attempt.catch !== 'function') return;

    attempt.catch(() => {
      // Autoplay was blocked (e.g. iOS Low Power Mode). The poster stays
      // visible; start the video on the first user interaction instead.
      this._armUserGestureStart();
    });
  }

  _armUserGestureStart() {
    if (this._gestureArmed) return;
    this._gestureArmed = true;

    const start = () => {
      this._video.play().catch(() => {});
      remove();
    };
    const remove = () => {
      events.forEach((type) => window.removeEventListener(type, start));
    };

    // `pointerdown` covers tap/click; `scroll`/`keydown` cover the rest.
    // { once: true } guards against double-firing if play() resolves.
    const events = ['pointerdown', 'touchstart', 'scroll', 'keydown'];
    events.forEach((type) =>
      window.addEventListener(type, start, { once: true, passive: true })
    );
  }
}

if (!customElements.get('video-banner')) {
  customElements.define('video-banner', VideoBanner);
}
